/*
  Removing things from the cart: Shipping Protection is never left on its own,
  the empty state is instant, and quick consecutive removes never take out the
  wrong item.

  BUG 1, reported and reproduced Sep 14 2026 - protection left on its own:

    Toggle Shipping Protection on, then remove the product. The product goes,
    the protection stays. The cart reads "Your Cart 1", "$2.99", the empty state
    never appears, and a shopper could reach checkout paying $2.99 to insure an
    order that does not exist.

  BUG 2, measured Sep 14 2026 - the empty state lagged:

    The first fix repaired the cart after the fact: the theme removed the
    product, the drawer drew the protection-only cart, and 700-1700ms later a
    second request removed the protection. Even with no protection, the drawer
    sat for about 620ms with no rows and a leftover upsell before the empty
    state arrived.

  BUG 3, reproduced Sep 14 2026 - the WRONG ITEM was removed:

    The theme removes a line by its POSITION ("line 2"), read from the row's
    data-index when the drawer was last rendered. The row you tap disappears at
    once (rv-ui-fixes.js fix 15), so the next row slides up under your finger,
    but every row keeps its old position number until the server re-render
    lands about 700ms later.

    Measured: cart = Hardshell Case, Silicone Case, Complete Bundle. Tap remove
    on the Case, then on the Silicone Case 150ms later. The second request said
    "line 2" to a cart that had already shifted: the Case and the $119 COMPLETE
    BUNDLE were removed, and the Silicone Case stayed.

    The live theme has the same race. The instant hide just makes it easy to hit.

  THE FIX

    1. One removal at a time. The first tap goes through the theme's own remove
       handler, untouched. A remove tapped while that is still in flight hides
       its row immediately and waits. When the re-render lands (the removed row
       leaves the DOM), the next waiting removal is replayed by clicking that
       product's FRESH remove button, found by variant id, so the theme sends a
       correct position. Waiting rows are re-hidden in every render, before
       paint, so they never flash back.

    2. The last product in the drawer: the theme's own empty state goes on screen
       on the same frame as the tap, and ONE request, POST /cart/clear.js with
       the drawer sections attached, empties the cart (protection included) and
       returns the new drawer, rendered through the theme's own renderContents().
       "Last" means every product row left has been tapped, so tapping through
       the whole cart quickly ends in a single clear. If another removal is still
       in flight, the empty state shows now and the clear waits for that render,
       so a late response cannot put a stale item back on screen.

    3. While a removal is in flight the quantity steppers and the Shipping
       Protection toggle are paused for those ~700ms (pointer-events). They also
       address lines by position - custom.js removes protection by position too -
       so they would hit the same race.

    4. Safety net: if any other route leaves a protection-only cart in the drawer,
       it is confirmed against /cart.js and then cleared. The confirmation is
       there because this path acts without a tap: it must never clear a cart
       because of a half-rendered drawer.

    5. The /cart page has no drawer to render into, so there it still checks
       /cart.js after changes and reloads once the cart has been cleared.

    If a removal never renders (network failure), after RENDER_TIMEOUT the
    waiting removals are dropped, hidden rows are shown again and the drawer is
    refreshed from the server, so the screen always ends up matching the cart.

  The empty-state markup below is the theme's own, copied from the server
  render. Whenever a real empty drawer is seen, the live markup replaces it, so a
  later edit to the empty state in the theme is picked up without touching this
  file.
*/
(function () {
  'use strict';

  var PROTECTION = /shipping[-_ ]?protection/i;
  var SECTIONS = 'cart-drawer,cart-icon-bubble';
  var LIST = 'cart-drawer-items, cart-items';
  var RENDER_TIMEOUT = 6000;

  var emptyHTML =
    '<div class="drawer__inner-empty"><div class="cart-drawer__warnings center">' +
    '<div class="cart-drawer__empty-content"><h2 class="cart__empty-text">Your cart is empty</h2>' +
    '<button class="drawer__close" type="button" onclick="this.closest(\'cart-drawer\').close()" aria-label="Close">' +
    '<span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" viewBox="0 0 18 17">' +
    '<path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"></path>' +
    '</svg></span></button>' +
    '<a href="/products/retroversepro" class="button button-box-shadow intensity-2 button-gradient-effect gradient-shine-enabled" style="color: var(--global_section_background_color) !important">Continue shopping</a>' +
    '</div></div></div>';

  var clearing = false;     /* a /cart/clear.js is in flight */
  var inflight = null;      /* the theme removal in flight: { row, inDrawer, timer } */
  var queue = [];           /* variant ids tapped while a removal was in flight */
  var pending = {};         /* variant id -> true: keep its row hidden until it is gone */
  var emptyAfter = false;   /* last product tapped while a removal was in flight */
  var confirming = false;

  function injectCSS() {
    if (document.getElementById('rv-protection-cleanup-css')) return;
    var s = document.createElement('style');
    s.id = 'rv-protection-cleanup-css';
    s.textContent =
      /* While the placeholder is up, everything except the empty state is hidden,
         exactly as the theme's own empty render looks. */
      'cart-drawer.rv-emptying .drawer__inner > :not(.drawer__inner-empty):not(style){display:none !important;}' +
      /* See THE FIX, point 3. */
      '.rv-cart-busy quantity-input,.rv-cart-busy .quantity,.rv-cart-busy .gb-shipping-protection-button{pointer-events:none !important;}' +
      /* While its own update is in flight the theme puts cart__items--disabled
         (pointer-events:none) on the whole item list, so a real tap on the next
         trash can fell straight through to the form behind it and did nothing.
         Measured: second tap 320ms after the first landed on the form, not the
         button. The remove buttons alone are made tappable again so that tap is
         queued by point 1 and replayed with a fresh position instead of lost.
         Quantity steppers stay disabled; they address lines by position. */
      '.cart__items--disabled cart-remove-button,.cart__items--disabled .cart-remove-button{pointer-events:auto !important;}';
    (document.head || document.documentElement).appendChild(s);
  }

  function rowHandle(row) {
    var a = row.querySelector('a[href*="/products/"]');
    if (!a) return null;
    var m = (a.getAttribute('href') || '').match(/\/products\/([^?#\/]+)/);
    if (!m) return null;
    try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
  }

  function variantOf(row) {
    var b = row.querySelector('cart-remove-button');
    return (b && b.getAttribute('data-id')) || row.getAttribute('data-variant-id') || '';
  }

  function isHidden(row) { return row.style.display === 'none'; }

  function hideRow(row) { row.style.setProperty('display', 'none', 'important'); }

  /* Rows whose link cannot be read count as products. That errs towards never
     clearing a cart that has something real in it. */
  function productRows(list) {
    var rows = list.querySelectorAll('.cart-item');
    var out = [];
    for (var i = 0; i < rows.length; i++) {
      var h = rowHandle(rows[i]);
      if (h && PROTECTION.test(h)) continue;
      out.push(rows[i]);
    }
    return out;
  }

  function currentList(inDrawer) {
    if (inDrawer) {
      var d = document.querySelector('cart-drawer');
      return d ? d.querySelector('cart-drawer-items') : null;
    }
    return document.querySelector('cart-items');
  }

  /* Lets rv-cart-upsell-rules.js re-run the moment rows are hidden here, rather
     than waiting for the next DOM change. */
  function notifyRowsChanged() {
    try { document.dispatchEvent(new CustomEvent('rv:cart-rows-changed')); } catch (e) {}
  }

  /* ---- empty-state placeholder ---- */

  function rememberEmpty(d) {
    var el = d.querySelector('.drawer__inner-empty:not([data-rv-placeholder])');
    if (el) emptyHTML = el.outerHTML;
  }

  function showPlaceholder(d) {
    var inner = d.querySelector('.drawer__inner');
    if (!inner) return;
    if (!inner.querySelector('[data-rv-placeholder]')) {
      var wrap = document.createElement('div');
      wrap.innerHTML = emptyHTML;
      var el = wrap.firstElementChild;
      if (!el) return;
      el.setAttribute('data-rv-placeholder', '1');
      inner.insertBefore(el, inner.firstChild);
    }
    d.classList.add('is-empty', 'rv-emptying');
  }

  function dropPlaceholder(d) {
    d.classList.remove('rv-emptying');
    var el = d.querySelector('[data-rv-placeholder]');
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }

  /* Hand over to the server's empty state once it is in the DOM. Until then,
     put the placeholder back if an intermediate render wiped it. */
  function settle(d) {
    if (d.querySelector('.drawer__inner-empty:not([data-rv-placeholder])')) dropPlaceholder(d);
    else if (!d.querySelector('[data-rv-placeholder]')) showPlaceholder(d);
  }

  function announce(state) {
    try {
      if (typeof window.publish === 'function' &&
          window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'rv-protection-cleanup', cartData: state });
      }
    } catch (e) {}
  }

  function clearCart(d) {
    if (clearing) return;
    clearing = true;
    queue = [];
    emptyAfter = false;
    showPlaceholder(d);

    fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ sections: SECTIONS, sections_url: window.location.pathname })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('clear ' + r.status);
        return r.json();
      })
      .then(function (state) {
        pending = {};
        if (state && state.sections && typeof d.renderContents === 'function') {
          d.renderContents(state);
        } else if (typeof d.refreshCartDrawer === 'function') {
          d.refreshCartDrawer();
        }
        settle(d);
        clearing = false;
        announce(state);
      })
      .catch(function () {
        clearing = false;
        pending = {};
        dropPlaceholder(d);
        d.classList.remove('is-empty');
        if (typeof d.refreshCartDrawer === 'function') {
          try { d.refreshCartDrawer(); } catch (e) {}
        }
      });
  }

  /* ---- one removal at a time ---- */

  function setBusy(on) {
    if (document.body) document.body.classList.toggle('rv-cart-busy', !!on);
  }

  function beginInflight(row, inDrawer) {
    inflight = { row: row, inDrawer: inDrawer, timer: setTimeout(abandon, RENDER_TIMEOUT) };
    setBusy(true);
  }

  function endInflight() {
    if (!inflight) return;
    clearTimeout(inflight.timer);
    inflight = null;
    setBusy(false);
  }

  function abandon() {
    var inDrawer = inflight && inflight.inDrawer;
    endInflight();
    queue = [];
    pending = {};
    emptyAfter = false;

    var list = currentList(inDrawer);
    if (list) {
      var rows = list.querySelectorAll('.cart-item');
      for (var i = 0; i < rows.length; i++) rows[i].style.removeProperty('display');
    }
    var d = document.querySelector('cart-drawer');
    if (d && inDrawer) {
      dropPlaceholder(d);
      if (typeof d.refreshCartDrawer === 'function') {
        try { d.refreshCartDrawer(); } catch (e) {}
      }
    }
    notifyRowsChanged();
  }

  function hidePending(list) {
    var rows = list.querySelectorAll('.cart-item');
    var seen = {};
    for (var i = 0; i < rows.length; i++) {
      var v = variantOf(rows[i]);
      if (pending[v]) { hideRow(rows[i]); seen[v] = true; }
    }
    for (var k in pending) {
      if (!seen[k]) delete pending[k];   /* gone from the cart */
    }
  }

  function freshButton(list, vid) {
    var rows = list.querySelectorAll('.cart-item');
    for (var i = 0; i < rows.length; i++) {
      if (variantOf(rows[i]) !== vid) continue;
      return rows[i].querySelector('cart-remove-button button') ||
             rows[i].querySelector('.cart-remove-button');
    }
    return null;
  }

  function replayNext(list) {
    while (queue.length) {
      var vid = queue.shift();
      var btn = freshButton(list, vid);
      if (btn) {
        btn.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
        return;
      }
      delete pending[vid];
    }
  }

  function stop(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  function onRemoveClick(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var btn = t.closest('cart-remove-button, .cart-remove-button');
    if (!btn) return;
    var list = btn.closest(LIST);
    var row = btn.closest('.cart-item');
    if (!list || !row) return;
    var d = list.closest('cart-drawer');

    /* The cart is being emptied anyway. */
    if (clearing) { stop(e); return; }

    var rows = productRows(list);
    var isProduct = rows.indexOf(row) > -1;
    var othersVisible = 0;
    for (var i = 0; i < rows.length; i++) {
      if (rows[i] !== row && !isHidden(rows[i])) othersVisible++;
    }

    /* ---- 2. the last product in the drawer ---- */
    if (d && isProduct && othersVisible === 0) {
      stop(e);
      hideRow(row);
      if (inflight) {
        emptyAfter = true;
        showPlaceholder(d);
      } else {
        clearCart(d);
      }
      notifyRowsChanged();
      return;
    }

    /* ---- 1. another removal is still in flight: wait for fresh positions ---- */
    if (inflight) {
      stop(e);
      hideRow(row);
      var vid = variantOf(row);
      if (vid && queue.indexOf(vid) === -1) {
        queue.push(vid);
        pending[vid] = true;
      }
      notifyRowsChanged();
      return;
    }

    /* Nothing in flight: the theme's own handler does this one. */
    beginInflight(row, !!d);
  }

  /* ---- 4. protection-only safety net, confirmed against the real cart ---- */
  function confirmOrphan(d) {
    if (confirming) return;
    confirming = true;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        confirming = false;
        if (!c || !c.items || !c.items.length) return;
        for (var i = 0; i < c.items.length; i++) {
          var it = c.items[i];
          if (!PROTECTION.test(it.handle || '') && !PROTECTION.test(it.product_title || '')) return;
        }
        if (!clearing && !inflight) clearCart(d);
      })
      .catch(function () { confirming = false; });
  }

  function onMutations() {
    var d = document.querySelector('cart-drawer');

    /* The removal in flight has rendered once its row has left the DOM. */
    if (inflight && !inflight.row.isConnected) {
      var inDrawer = inflight.inDrawer;
      endInflight();
      var fresh = currentList(inDrawer);
      if (fresh) hidePending(fresh);
      if (emptyAfter && d && inDrawer) {
        clearCart(d);
      } else if (fresh) {
        replayNext(fresh);
      }
      notifyRowsChanged();
    } else {
      var shown = currentList(true);
      if (shown) {
        for (var k in pending) { hidePending(shown); break; }
      }
    }

    if (!d) return;
    if (d.classList.contains('rv-emptying')) { settle(d); return; }
    if (d.classList.contains('is-empty')) { rememberEmpty(d); return; }
    if (clearing || inflight) return;
    var dl = currentList(true);
    if (!dl || productRows(dl).length) return;
    confirmOrphan(d);
  }

  /* ---- 5. the /cart page ---- */
  function checkCartPage() {
    if (clearing || !document.querySelector('#main-cart-items')) return;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        if (!c || !c.items || !c.items.length) return;
        for (var i = 0; i < c.items.length; i++) {
          var it = c.items[i];
          if (!PROTECTION.test(it.handle || '') && !PROTECTION.test(it.product_title || '')) return;
        }
        clearing = true;
        return fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' })
          .then(function () { window.location.reload(); });
      })
      .catch(function () { clearing = false; });
  }

  function start() {
    injectCSS();
    document.addEventListener('click', onRemoveClick, true);

    if (window.MutationObserver) {
      var mo = new MutationObserver(onMutations);
      var d = document.querySelector('cart-drawer');
      if (d) mo.observe(d, { childList: true, subtree: true });
      var page = document.querySelector('#main-cart-items');
      if (page) mo.observe(page, { childList: true, subtree: true });
    }
    onMutations();

    if (document.querySelector('#main-cart-items')) {
      try {
        if (typeof window.subscribe === 'function' &&
            window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
          window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (payload) {
            if (payload && payload.source === 'rv-protection-cleanup') return;
            setTimeout(checkCartPage, 300);
          });
        }
      } catch (e) {}
      checkCartPage();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
